const isLocalhost =
  typeof window !== 'undefined' && window.location?.hostname === 'localhost';

// Provide a sensible default proxy in development to avoid noisy CORS failures
// when hitting production APIs from localhost. The environment variable always
// takes precedence so teams can supply their own proxy implementation.
const DEFAULT_DEV_PROXY = 'https://api.allorigins.win/raw?url=';

// Hosts that are on this machine. A public proxy resolves 127.0.0.1 to *itself*,
// so proxying a local target can never work — and it would also hand the URL to
// a third party. Running the API locally is the normal dev setup, so this must
// short-circuit before any proxy is applied.
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]', '::1']);

const isLocalTarget = (url) => {
  if (typeof url !== 'string') return false;
  // Relative URLs are same-origin by definition — never proxy them.
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) return true;
  try {
    return LOCAL_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
};

/**
 * URL for an <img src>. Deliberately never proxied.
 *
 * CORS governs fetch/XHR and canvas readback — it does not govern displaying an
 * image. Routing artwork through a public CORS proxy therefore buys nothing and
 * costs a lot: every thumbnail becomes a round trip through a third party, the
 * proxy rate-limits or stalls under load, and the request leaks which media the
 * visitor is viewing. Point <img> at the real origin.
 */
export const imageSrc = (url) => url;

export const withCors = (url) => {
  if (isLocalTarget(url)) return url;

  const proxy =
    process.env.REACT_APP_CORS_PROXY ||
    (process.env.NODE_ENV !== 'production' && isLocalhost
      ? DEFAULT_DEV_PROXY
      : '');

  if (!proxy) return url;

  // Support proxies where `%s` acts as placeholder for encoded URL
  if (proxy.includes('%s')) {
    return proxy.replace('%s', encodeURIComponent(url));
  }

  const needsEncoding = proxy.includes('?');

  // Default behaviour: append to proxy prefix. Some proxies expect the raw
  // URL (e.g. cors-anywhere style), while query-string based proxies need an
  // encoded value to remain valid. Detect simple heuristics so we "just work"
  // in both environments.
  return needsEncoding ? `${proxy}${encodeURIComponent(url)}` : `${proxy}${url}`;
};
